
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to handle Stripe Connect operations (connect and disconnect)
 */
export function useStripeConnect() {
  const [isConnecting, setIsConnecting] = useState(false);
  const { toast } = useToast();

  const handleConnect = async () => {
    setIsConnecting(true);
    
    try {
      console.log('Initiating Stripe Connect flow...');
      
      // First ensure we have a valid session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('Auth session error when connecting to Stripe:', sessionError || 'No session found');
        toast({
          title: "Authentication Error",
          description: "You must be logged in to connect your Stripe account",
          variant: "destructive"
        });
        setIsConnecting(false);
        return;
      }
      
      console.log('Valid session found for Stripe connect, token length:', 
                 sessionData.session.access_token.length);

      // Call the edge function to generate connect URL and update state
      const { data, error } = await supabase.functions.invoke('connect-stripe', {
        body: { action: 'connect' },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });

      console.log('Response from connect-stripe function:', JSON.stringify(data));

      if (error) {
        console.error('Error connecting to Stripe:', error);
        toast({
          title: "Connection Error",
          description: error.message || "Could not connect to Stripe. Please try again.",
          variant: "destructive"
        });
        setIsConnecting(false);
        return;
      } else if (data?.url) {
        // Verify that state was saved by checking for confirmation in the response
        if (!data.stateSaved) {
          console.error('State was not saved before redirect, aborting to prevent auth errors');
          toast({
            title: "Connection Error",
            description: "Failed to prepare for Stripe connection. Please try again.",
            variant: "destructive"
          });
          setIsConnecting(false);
          return;
        }
        
        // Perform an explicit verification that the state was actually saved
        console.log('Verifying state was saved in profile...');
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('stripe_connect_state')
          .eq('id', sessionData.session.user.id)
          .single();
          
        console.log('Profile verification result:', JSON.stringify({
          hasData: !!profileData,
          savedState: profileData?.stripe_connect_state,
          expectedState: data.state,
          error: profileError ? profileError.message : null
        }));
        
        if (profileError || !profileData || profileData.stripe_connect_state !== data.state) {
          console.error('State verification failed. Expected:', data.state, 
                       'Got:', profileData?.stripe_connect_state);
          toast({
            title: "Connection Error",
            description: "Failed to securely prepare for Stripe connection. Please try again.",
            variant: "destructive"
          });
          setIsConnecting(false);
          return;
        }
        
        // Log the URL and state for debugging
        console.info('Redirecting to Stripe Connect URL:', data.url);
        console.info('State parameter saved:', data.state);
        console.info('State verification successful!');
        
        // Add a small delay to ensure the log is visible
        setTimeout(() => {
          // Only redirect to Stripe Connect if state was successfully saved
          window.location.href = data.url;
        }, 1000);
        
        return; // Early return to prevent setting isConnecting to false
      } else if (data?.error) {
        console.error('Stripe Connect error:', data.error, data.details || 'No additional details');
        toast({
          title: "Configuration Error",
          description: data.details ? `${data.error}: ${data.details}` : data.error,
          variant: "destructive"
        });
      } else {
        console.error('No URL returned from connect-stripe function');
        toast({
          title: "Connection Error",
          description: "Could not get Stripe Connect URL. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error connecting to Stripe:', error);
      toast({
        title: "Connection Error",
        description: error.message || "Could not connect to Stripe. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsConnecting(true);
    
    try {
      console.log('Initiating Stripe disconnect flow...');
      
      // First ensure we have a valid session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('Auth session error when disconnecting from Stripe:', sessionError || 'No session found');
        toast({
          title: "Authentication Error",
          description: "You must be logged in to disconnect your Stripe account",
          variant: "destructive"
        });
        return;
      }
      
      console.log('Valid session found for Stripe disconnect');

      const { data, error } = await supabase.functions.invoke('connect-stripe', {
        body: { action: 'disconnect' },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });

      console.log('Response from disconnect:', data);

      if (error) {
        console.error('Error disconnecting from Stripe:', error);
        toast({
          title: "Disconnection Error",
          description: error.message || "Could not disconnect from Stripe. Please try again.",
          variant: "destructive"
        });
      } else if (data?.error) {
        console.error('Stripe disconnect error:', data.error);
        toast({
          title: "Disconnection Error",
          description: data.error || "Could not disconnect from Stripe. Please try again.",
          variant: "destructive"
        });
      } else {
        // Clear the cached connection status from localStorage
        localStorage.removeItem('stripeConnected');
        localStorage.removeItem('stripeAccountId');
        localStorage.removeItem('stripeStatusTimestamp');

        const event = new CustomEvent('stripe-disconnected');
        document.dispatchEvent(event);

        toast({
          title: "Stripe Disconnected",
          description: "Your Stripe account has been successfully disconnected.",
        });
      }
    } catch (error: any) {
      console.error('Error disconnecting from Stripe:', error);
      toast({
        title: "Disconnection Error",
        description: error.message || "Could not disconnect from Stripe. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsConnecting(false);
    }
  };

  return {
    isConnecting,
    handleConnect,
    handleDisconnect
  };
}
